import styled, { css, keyframes } from 'styled-components';

import { resetButton, withDecoratedScroll } from 'styles/common';

// ── Bell animation ────────────────────────────────────────────────────────────

const bellRing = keyframes`
  0%, 100% { transform: rotate(0deg); }
  10% { transform: rotate(14deg); }
  20% { transform: rotate(-14deg); }
  30% { transform: rotate(10deg); }
  40% { transform: rotate(-10deg); }
  50% { transform: rotate(6deg); }
  60% { transform: rotate(-4deg); }
  70% { transform: rotate(0deg); }
`;

const glowPulse = keyframes`
  0%, 100% { box-shadow: 0 0 8px rgba(255, 200, 50, 0.3), inset 0 0 6px rgba(255, 200, 50, 0.05); }
  50% { box-shadow: 0 0 18px rgba(255, 200, 50, 0.5), inset 0 0 10px rgba(255, 200, 50, 0.1); }
`;

// ── Button ────────────────────────────────────────────────────────────────────

export const NewsButton = styled.button<{ $active: boolean }>`
  ${resetButton}
  height: 32px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0px 16px;
  border-radius: 8px;
  font-family: 'Rajdhani', sans-serif;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.5px;
  transition: all 0.3s ease;
  position: relative;
  white-space: nowrap;

  ${({ $active }) =>
    $active
      ? css`
          background: radial-gradient(
            circle at 35% 30%,
            rgba(255, 200, 50, 0.25),
            rgba(40, 20, 0, 0.9)
          );
          border: 1px solid rgba(255, 200, 50, 0.7);
          color: rgba(255, 230, 150, 0.95);
          animation: ${glowPulse} 2.5s ease-in-out infinite;

          &:hover {
            border-color: rgba(255, 200, 50, 1);
            box-shadow:
              0 0 22px rgba(255, 200, 50, 0.5),
              inset 0 0 14px rgba(255, 200, 50, 0.15);
          }
        `
      : css`
          background: rgba(255, 255, 255, 0.04);
          border: 1px solid rgba(255, 255, 255, 0.12);
          color: rgba(255, 255, 255, 0.45);

          &:hover {
            background: rgba(255, 255, 255, 0.08);
            border-color: rgba(255, 255, 255, 0.2);
            color: rgba(255, 255, 255, 0.7);
          }
        `}

  &:active {
    transform: scale(0.95);
  }

  @media (max-width: 600px) {
    height: 32px;
    padding: 0px 12px;
    font-size: 12px;
  }
`;

export const BellWrapper = styled.span`
  margin-left: -4px;
  display: flex;
  align-items: center;

  svg {
    width: 18px;
    height: 18px;
    animation: ${bellRing} 2s ease-in-out infinite;

    path {
      fill: rgba(255, 200, 50, 0.9);
    }
  }
`;

export const Badge = styled.span`
  position: absolute;
  top: -8px;
  right: -3px;
  background: #ff4444;
  color: #fff;
  font-family: 'Rajdhani', sans-serif;
  font-size: 9px;
  font-weight: 700;
  padding: 2px 4px 0px;
  border-radius: 6px;
  line-height: 1.2;
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

// ── Modal content ─────────────────────────────────────────────────────────────

export const ModalContent = styled.div`
  ${withDecoratedScroll}
  max-height: calc(80vh - 80px);
  overflow-y: auto;
  padding-right: 4px;
`;

export const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  padding-right: 40px;
`;

export const ModalTitle = styled.h2`
  margin: 0;
  font-family: 'Rajdhani', sans-serif;
  font-size: 22px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.9);
  letter-spacing: 0.5px;
`;

export const RefreshButton = styled.button`
  ${resetButton}
  padding: 6px 14px;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.6);
  font-family: 'Rajdhani', sans-serif;
  font-size: 13px;
  font-weight: 600;
  transition: all 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: rgba(255, 255, 255, 0.85);
  }
`;

// ── Story card ────────────────────────────────────────────────────────────────

export const StoryCard = styled.div`
  margin-bottom: 32px;

  &:last-child {
    margin-bottom: 0;
  }
`;

export const StoryTitle = styled.h3`
  margin: 0 0 8px;
  font-family: 'Rajdhani', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.3;
`;

export const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 12px;
`;

export const Tag = styled.span`
  padding: 2px 8px;
  border-radius: 4px;
  background: rgba(155, 89, 182, 0.15);
  border: 1px solid rgba(155, 89, 182, 0.25);
  color: rgba(200, 160, 230, 0.9);
  font-family: 'Share Tech Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.3px;
`;

export const EpisodeList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const Episode = styled.div`
  padding: 16px 12px 10px;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.2);
  border-left: 2px solid rgba(155, 89, 182, 0.3);
  border-bottom: 2px solid rgba(155, 89, 182, 0.3);
`;

export const EpisodeSummary = styled.p`
  margin: 0 0 6px;
  font-family: 'Roboto', sans-serif;
  font-size: 15px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.75);
  line-height: 1.7;

  @media (max-width: 600px) {
    font-size: 13px;
  }
`;

export const EpisodeDate = styled.span`
  font-family: 'Share Tech Mono', monospace;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.3);
`;

// ── Divider ───────────────────────────────────────────────────────────────────

export const ReadDivider = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 24px 0 16px;
  font-family: 'Share Tech Mono', monospace;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.3);
  letter-spacing: 1px;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: rgba(255, 255, 255, 0.1);
  }
`;

export const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  font-family: 'Rajdhani', sans-serif;
  font-size: 15px;
  color: rgba(255, 255, 255, 0.35);
`;
